import * as React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  Tooltip,
} from '@mui/material';
import { unstable_useId as useId } from '@mui/material/utils';

import styles from './ChipSelect.module.css';

export interface ChipOption {
  value: string;
  label: string;
  /** Shown on hover. */
  hint?: string;
  disabled?: boolean;
}

export interface ChipSelectProps {
  label: string;
  options: ChipOption[];
  selected: string[];
  onToggle: (value: string) => void;
  helperText?: React.ReactNode;
  error?: string;
  /** Renders bulk select / clear actions when there are many options. */
  onSelectAll?: () => void;
  onClear?: () => void;
  emptyText?: string;
}

/** Multi-select as a wrap of toggleable chips. Each chip is a checkbox for assistive tech. */
const ChipSelect: React.FC<ChipSelectProps> = ({
  label,
  options,
  selected,
  onToggle,
  helperText,
  error,
  onSelectAll,
  onClear,
  emptyText = 'Nothing to choose from yet',
}) => {
  const labelId = useId();
  const selectedSet = new Set(selected);
  return (
    <FormControl component="fieldset" error={!!error} fullWidth>
      <Box className={styles.header}>
        <FormLabel component="legend" id={labelId}>
          {label}
          <span className={styles.count}>
            {options.length > 0 && ` · ${selected.length} of ${options.length}`}
          </span>
        </FormLabel>
        {options.length > 3 && (onSelectAll || onClear) && (
          <Box className={styles.bulk}>
            {onSelectAll && (
              <Chip size="small" variant="outlined" label="Select all" onClick={onSelectAll} />
            )}
            {onClear && (
              <Chip size="small" variant="outlined" label="Clear" onClick={onClear} />
            )}
          </Box>
        )}
      </Box>
      <Box role="group" aria-labelledby={labelId} className={styles.chips}>
        {options.length === 0 && (
          <FormHelperText className={styles.empty}>{emptyText}</FormHelperText>
        )}
        {options.map((option) => {
          const checked = selectedSet.has(option.value);
          const chip = (
            <Chip
              key={option.value}
              role="checkbox"
              aria-checked={checked}
              aria-disabled={option.disabled || undefined}
              label={option.label}
              icon={checked ? <CheckIcon /> : undefined}
              color={checked ? 'primary' : 'default'}
              variant={checked ? 'filled' : 'outlined'}
              disabled={option.disabled}
              onClick={() => onToggle(option.value)}
            />
          );
          return option.hint ? (
            <Tooltip key={option.value} title={option.hint}>
              <span>{chip}</span>
            </Tooltip>
          ) : (
            chip
          );
        })}
      </Box>
      {(error || helperText) && <FormHelperText>{error || helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ChipSelect;
